import Button from 'components/Button'
import { DialogIllustration } from 'components/Dialog'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSection,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { observer } from 'mobx-react'
import ReviewPromptDialogViewModel, {
  DeletePromptDialogViewModel,
} from 'jobs/prompts/review/ReviewPromptDialogViewModel'
import React from 'react'
import styles from './ReviewPromptDialog.sass'
import ErrorBlock from 'components/ErrorBlock'
import { getQuestionComponentRegistryEntry } from 'jobs/questions/component-registry'

interface ReviewPromptDialogProps {
  vm: ReviewPromptDialogViewModel
}

export default observer(function ReviewPromptDialog({
  vm,
}: ReviewPromptDialogProps) {
  const question = vm.questionVM
  if (!question) {
    return null
  }

  const isAnswered = vm.prompt?.dateAnswered != null
  const { EditorInputComponent } = getQuestionComponentRegistryEntry(
    question.type,
  )

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <DialogIllustration src={getDialogIllustration(question.type)} />
          <ModalSheetContentTitle dataTest="prompt-review-title">
            {vm.questionVM?.question}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription dataTest="prompt-review-description">
            {vm.questionVM?.description}
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>

        <ModalSheetContentBlock
          dataTest={'prompt-review-question-' + vm.prompt?.question?.type}
        >
          {vm.questionVM && (
            <EditorInputComponent
              vm={vm.questionVM}
              disabled={!vm.prompt?.canAnswer}
            />
          )}
        </ModalSheetContentBlock>
      </ModalSheetContent>
      {vm.answerPrompt.rejected && (
        <ErrorBlock tackOn="bottom" error={vm.answerPrompt.error} />
      )}
      <ModalSheetFooter>
        {isAnswered && (
          <ModalSheetFooterSection>
            <p className={styles.footerText}>
              {getCompletionInfo(
                vm.prompt?.answeredBy?.displayName,
                vm.prompt?.dateAnswered,
              )}
            </p>
          </ModalSheetFooterSection>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            dataTest="prompt-close-button"
          >
            {!vm.prompt?.canAnswer ? 'Close' : 'Not Now'}
          </Button>

          {vm.prompt?.canAnswer && (
            <Button
              type="primary"
              dataTest="prompt-submit-button"
              loading={vm.answerPrompt.pending}
              onClick={vm.answerPrompt}
            >
              Submit
            </Button>
          )}
          {vm.prompt?.canDelete && (
            <Button
              type="danger"
              dataTest="prompt-delete-button"
              loading={vm.deletePromptVM.deletePrompt.pending}
              onClick={() => vm.deletePromptVM.show()}
            >
              Delete Prompt
            </Button>
          )}
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      <DeletePromptDialog vm={vm.deletePromptVM} />
    </ModalSheet>
  )
})

interface DeletePromptDialogProps {
  vm: DeletePromptDialogViewModel
}

const DeletePromptDialog = observer(function DeletePromptDialog({
  vm,
}: DeletePromptDialogProps) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeader withCloseButton></ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <DialogIllustration
            src={require('assets/images/img_missing-permissions.svg')}
          />
          <ModalSheetContentTitle dataTest="delete-dialog-title">
            Are you sure you want to delete this prompt?
          </ModalSheetContentTitle>
          <ModalSheetContentDescription dataTest="delete-dialog-description">
            This action cannot be undone. Are you sure that you want to
            permanently delete this prompt?
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button
              type="default"
              onClick={vm.close}
              dataTest="delete-dialog-close-button"
            >
              Close
            </Button>
            <Button
              type="danger"
              dataTest="prompt-delete-button"
              loading={vm.deletePrompt.pending}
              onClick={vm.deletePrompt}
            >
              Delete Forever
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheetContent>
    </ModalSheet>
  )
})

const dateFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'short' })
const timeFormatter = new Intl.DateTimeFormat('en-US', { timeStyle: 'short' })

function getCompletionInfo(answeredByName?: string, date?: Date | null) {
  if (!date || !answeredByName) {
    return ''
  }

  const day = dateFormatter.format(date)
  const time = timeFormatter.format(date)
  return (
    <>
      Completed by <strong>{answeredByName}</strong> on <strong>{day}</strong>{' '}
      at <strong>{time}</strong>
    </>
  )
}

function getDialogIllustration(type: string) {
  switch (type) {
    case 'DOCUMENTS':
      return require('assets/images/img_upload-files.svg')
    case 'TEXT':
      return require('assets/images/img_create-name.svg')
    case 'BOOL':
    default:
      return require('assets/images/img_send-prompt-message.svg')
  }
}
